import React from "react";
import { gaTrackCta } from "../../../analytics/ga4";
import { pageGutterPx } from "../../../core/breakpoints";
import { C, DASHBOARD_QUICK_ACTIONS } from "../../../core/constants";
import { analyticsWindowOptions } from "../../../core/analyticsWindows";
import { APP_FONT_SIZE } from "../../../core/theme";
import { useIsMobile } from "../../../hooks/useMediaQuery";
import useDashboardForecast from "../useDashboardForecast";
import GlassCard from "../../../ui/GlassCard";
import AnalyticsWindowSelector from "../../../ui/AnalyticsWindowSelector";
import ReadingReplayListItem from "../../predictions/components/ReadingReplayListItem";

function GoldText({ children, accent }) {
  return <span style={{ color: accent, textShadow: `0 0 18px ${accent}66` }}>{children}</span>;
}

function forecastSourceLabel(source, t) {
  const raw = String(source || "").trim();
  if (!raw || /fallback|deterministic/i.test(raw)) return t("dashboard_forecast_source_today");
  if (raw.startsWith("natal-transit")) return t("dashboard_forecast_source_sky");
  return raw;
}

export default function DashboardHome({ acc, setPage, dashboardMeta, t, locale, formatInTimezone }) {
  const fs = APP_FONT_SIZE;
  const isMobile = useIsMobile();
  const gutter = pageGutterPx(isMobile);
  const activeSubscription = Boolean(dashboardMeta?.activeSubscription);
  const { loading, error, data, windowKey, setWindowKey, hasData } = useDashboardForecast({ activeSubscription });
  const statsWindowOptions = analyticsWindowOptions(activeSubscription);

  const forecastCards = data?.forecast_cards || [];
  const recentReadings = (data?.recent_activity || []).slice(0, 5);

  const openReadingHistoryFooter = () => {
    if (activeSubscription) {
      gaTrackCta({
        cta_id: "dashboard_view_reading_history",
        cta_label: t("dashboard_recent_readings_view_all"),
        destination: "readingHistory",
        screen_class: "dashboard",
      });
      setPage("readingHistory");
      return;
    }
    gaTrackCta({
      cta_id: "dashboard_reading_history_upsell",
      cta_label: t("dashboard_recent_readings_upsell_cta"),
      destination: "creditsPlans",
      screen_class: "dashboard",
    });
    setPage("creditsPlans");
  };

  const quickStats = [
    { label: t("dashboard_stat_total_predictions"), val: data?.quick_stats?.total_predictions ?? "—", icon: "⊕", color: acc.primary },
    { label: t("dashboard_stat_credits_spent"), val: data?.quick_stats?.credits_spent ?? "—", icon: "✦", color: acc.secondary },
    {
      label: t("dashboard_stat_favorite_feature"),
      val: loading ? "—" : data?.quick_stats?.favorite_feature || t("dashboard_stat_empty"),
      icon: "◎",
      color: acc.tertiary,
    },
    { label: t("dashboard_stat_streak_days"), val: data?.quick_stats?.streak_days ?? "0", icon: "☾", color: acc.primary },
  ];

  return (
    <div style={{ padding: gutter }}>
      <div style={{ marginBottom: 32 }}>
        <h1 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: "clamp(28px,3vw,40px)", fontWeight: 400, color: C.textPrimary }}>
          {dashboardMeta.greeting}, <GoldText accent={acc.secondary}>{dashboardMeta.displayName}</GoldText>
        </h1>
        <p style={{ color: C.textSecondary, fontSize: fs, marginTop: 6 }}>
          {t("dashboard_subtitle_today")}, {dashboardMeta.shortDate} · {t("dashboard_subtitle_sun_in")} {dashboardMeta.sunSign}
        </p>
      </div>

      <AnalyticsWindowSelector
        windowKey={windowKey}
        options={statsWindowOptions}
        onChange={setWindowKey}
        loading={loading}
        hasData={hasData}
        acc={acc}
        t={t}
      />

      <div
        className="ecliptica-card-grid"
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))",
          gap: 16,
          marginBottom: 32,
          opacity: loading && hasData ? 0.65 : 1,
          pointerEvents: loading && hasData ? "none" : "auto",
        }}
      >
        {quickStats.map((s, i) => (
          <GlassCard key={i} style={{ padding: "20px 22px" }} accent={s.color} glow>
            <div style={{ fontSize: 22, color: s.color, marginBottom: 8 }}>{s.icon}</div>
            <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 32, fontWeight: 500, color: C.textPrimary }}>{s.val}</div>
            <div style={{ fontSize: 12, color: C.textMuted, marginTop: 4, letterSpacing: "0.05em" }}>{s.label}</div>
          </GlassCard>
        ))}
      </div>

      <div style={{ marginBottom: 8 }}>
        <h2 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 26, fontWeight: 400, color: C.textPrimary, marginBottom: 20 }}>{t("dashboard_today_cosmic_forecast")}</h2>
        {loading && (
          <GlassCard style={{ padding: 20, marginBottom: 16 }} accent={acc.primary}>
            <div style={{ fontSize: fs - 1, color: C.textSecondary }}>{t("dashboard_forecast_loading")}</div>
          </GlassCard>
        )}
        {!loading && error && (
          <GlassCard style={{ padding: 20, marginBottom: 16 }} accent={acc.secondary}>
            <div style={{ fontSize: fs - 1, color: C.textSecondary }}>{t(error)}</div>
          </GlassCard>
        )}
        <div className="ecliptica-card-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(280px,1fr))", gap: 20 }}>
          {!loading && !error && forecastCards.length === 0 && (
            <GlassCard hoverable style={{ padding: 28 }} accent={acc.primary}>
              <h3 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 20, fontWeight: 500, color: C.textPrimary, marginBottom: 10 }}>
                {t("dashboard_forecast_empty_title")}
              </h3>
              <p style={{ fontSize: fs - 1, color: C.textSecondary, lineHeight: 1.65 }}>
                {t("dashboard_forecast_empty_text")}
              </p>
            </GlassCard>
          )}
          {forecastCards.map((p, i) => {
            const sourceLabel = forecastSourceLabel(p.source, t);
            return (
            <GlassCard key={i} hoverable style={{ padding: 28 }} accent={acc.primary} glow={p.confidence >= 88}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
                <div style={{ fontSize: 22, color: acc.primary }}>{p.icon}</div>
                {p.confidence >= 88 && <div style={{ fontSize: 10, letterSpacing: "0.15em", color: acc.secondary, background: `${acc.secondary}18`, padding: "3px 8px", borderRadius: 8 }}>{t("dashboard_forecast_highlighted")}</div>}
              </div>
              <h3 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 20, fontWeight: 500, color: C.textPrimary, marginBottom: 10 }}>{t(p.title)}</h3>
              <p style={{ fontSize: fs - 1, color: C.textSecondary, lineHeight: 1.65, marginBottom: p.key_notice ? 12 : 0 }}>{p.summary}</p>
              {p.key_notice && (
                <div
                  style={{
                    marginTop: 4,
                    padding: "10px 12px",
                    borderRadius: 10,
                    background: `${acc.secondary}0c`,
                    borderLeft: `3px solid ${acc.secondary}`,
                  }}
                >
                  <div style={{ fontSize: 10, color: acc.secondary, letterSpacing: "0.1em", marginBottom: 5, textTransform: "uppercase" }}>
                    {t("dashboard_forecast_key_notice_label")}
                  </div>
                  <div style={{ fontSize: fs - 1, color: C.textPrimary, lineHeight: 1.55 }}>{p.key_notice}</div>
                </div>
              )}
              <div style={{ marginTop: 18, borderTop: "1px solid rgba(255,255,255,0.06)", paddingTop: 14 }}>
                <span style={{ fontSize: 12, color: acc.primary, letterSpacing: "0.05em" }}>
                  {t("dashboard_forecast_confidence")}: {p.confidence}%{sourceLabel ? ` · ${sourceLabel}` : ""}
                </span>
              </div>
            </GlassCard>
            );
          })}
        </div>
      </div>

      <div style={{ marginTop: 24 }}>
        <h2 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 24, fontWeight: 400, color: C.textPrimary, marginBottom: 12 }}>
          {t("dashboard_recent_readings")}
        </h2>
        <div style={{ color: C.textMuted, fontSize: 12, marginBottom: 10 }}>
          {t(activeSubscription ? "dashboard_recent_readings_limit_note" : "dashboard_recent_readings_limit_note_free")}
        </div>
        <GlassCard style={{ padding: 20 }} accent={acc.tertiary}>
          {recentReadings.length === 0 ? (
            <div style={{ color: C.textSecondary, fontSize: fs - 1 }}>
              {t("dashboard_recent_readings_empty")}
            </div>
          ) : (
            recentReadings.map((item, idx) => (
              <ReadingReplayListItem
                key={item.prediction_id || idx}
                item={item}
                isLast={idx === recentReadings.length - 1}
                acc={acc}
                t={t}
                locale={locale}
                formatInTimezone={formatInTimezone}
                setPage={setPage}
                screenClass="dashboard"
                ctaIdPrefix="dashboard_recent_replay"
              />
            ))
          )}

          <button
            type="button"
            onClick={openReadingHistoryFooter}
            style={{
              width: "100%",
              marginTop: recentReadings.length > 0 ? 6 : 12,
              padding: recentReadings.length > 0 ? "12px 2px 0" : "0 2px",
              border: "none",
              borderTop: recentReadings.length > 0 ? "1px solid rgba(255,255,255,0.06)" : "none",
              background: "transparent",
              textAlign: "left",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
              cursor: "pointer",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
              <span
                aria-hidden
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 10,
                  display: "grid",
                  placeItems: "center",
                  fontSize: 15,
                  color: activeSubscription ? acc.tertiary : acc.secondary,
                  background: activeSubscription ? `${acc.tertiary}14` : `${acc.secondary}12`,
                  border: `1px solid ${activeSubscription ? `${acc.tertiary}33` : `${acc.secondary}33`}`,
                  flexShrink: 0,
                }}
              >
                {activeSubscription ? "☾" : "✦"}
              </span>
              <div style={{ minWidth: 0 }}>
                <div style={{ color: C.textPrimary, fontSize: fs - 2, fontWeight: 500 }}>
                  {activeSubscription
                    ? t("dashboard_recent_readings_view_all")
                    : t("dashboard_recent_readings_upsell_title")}
                </div>
                <div style={{ color: C.textMuted, fontSize: 11, marginTop: 2, lineHeight: 1.4 }}>
                  {activeSubscription
                    ? t("dashboard_recent_readings_footer_subscriber")
                    : t("dashboard_recent_readings_upsell_body")}
                </div>
              </div>
            </div>
            <span style={{ color: activeSubscription ? acc.tertiary : acc.secondary, fontSize: 12, whiteSpace: "nowrap" }}>
              {activeSubscription ? "→" : `${t("dashboard_recent_readings_upsell_cta")} →`}
            </span>
          </button>
        </GlassCard>
      </div>

      <div style={{ marginTop: 32 }}>
        <h2
          style={{
            fontFamily: "'Cormorant Garamond',serif",
            fontSize: 24,
            fontWeight: 400,
            color: C.textPrimary,
            marginBottom: 16,
          }}
        >
          {t("dashboard_quick_actions_title")}
        </h2>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "repeat(2, minmax(0, 1fr))" : "repeat(auto-fit, minmax(150px, 1fr))",
            gap: 12,
          }}
        >
          {DASHBOARD_QUICK_ACTIONS.map((action) => {
            const label = t(action.labelKey);
            return (
              <button
                key={action.id}
                type="button"
                onClick={() => {
                  gaTrackCta({
                    cta_id: action.ctaId,
                    cta_label: label,
                    destination: action.id,
                    screen_class: "dashboard",
                  });
                  setPage(action.id);
                }}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 8,
                  minHeight: 88,
                  padding: "14px 12px",
                  borderRadius: 12,
                  border: `1px solid ${acc.primary}33`,
                  background: "rgba(255,255,255,0.03)",
                  color: C.textPrimary,
                  cursor: "pointer",
                  font: "inherit",
                  fontSize: fs - 2,
                  lineHeight: 1.35,
                  textAlign: "center",
                }}
              >
                <span style={{ fontSize: 22, color: acc.primary, lineHeight: 1 }}>{action.icon}</span>
                <span>{label}</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
